// Save/load. The original packed a XOR-obfuscated, zlib-compressed binary blob;
// the web port serialises the same state to JSON, then gzip + base64 (v1), which
// is stored server-side. Because the full map is persisted, loaded games need no
// RNG parity with the native build.

import pako from 'pako';
import { G, rooms, map } from '../core/state';
import { getActiveWorld } from '../core/world';
import { S, RES_SCALE } from '../core/constants';
import { RoomConnection } from '../core/types';
import { serializeCreatures, loadCreatures } from '../core/demon';
import { SavingScreen } from '../core/screens';

interface ConnDTO {
  x: number;
  y: number;
  x2: number;
  y2: number;
  c: number;
}
interface RoomDTO {
  x: number;
  y: number;
  w: number;
  h: number;
  creator: number;
  visited: number;
  checkpoint: number;
  s_dist: number;
  connections: number;
  room_type: number;
  room_param: number;
  con: ConnDTO[];
}
interface SaveData {
  v: number;
  w: number;
  h: number;
  totalRooms: number;
  place_of_power: number;
  mapM: string; // base64 of raw tile bytes
  mapR: string; // base64 of raw Int32 room-id bytes
  rooms: RoomDTO[];
  creatures: ReturnType<typeof serializeCreatures>;
  player: Record<string, number>;
  artifacts: number[];
}

let pending: SaveData | null = null;

function u8ToB64(u8: Uint8Array): string {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    s += String.fromCharCode.apply(null, Array.from(u8.subarray(i, i + chunk)));
  }
  return btoa(s);
}

function b64ToU8(b64: string): Uint8Array {
  const s = atob(b64);
  const u8 = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) u8[i] = s.charCodeAt(i);
  return u8;
}

// --- Serialisation (equivalent to SaveGame) ------------------------------
export function serializeGame(): string {
  const roomDTOs: RoomDTO[] = [];
  for (let i = 0; i < map.totalRooms; i++) {
    const rm = rooms[i];
    const con: ConnDTO[] = [];
    let rt: RoomConnection | null = rm.con;
    while (rt !== null) {
      con.push({ x: rt.x, y: rt.y, x2: rt.x2, y2: rt.y2, c: rt.c });
      rt = rt.n;
    }
    roomDTOs.push({
      x: rm.x,
      y: rm.y,
      w: rm.w,
      h: rm.h,
      creator: rm.creator,
      visited: rm.visited,
      checkpoint: rm.checkpoint,
      s_dist: rm.s_dist,
      connections: rm.connections,
      room_type: rm.room_type,
      room_param: rm.room_param,
      con,
    });
  }

  const data: SaveData = {
    v: 2,
    w: map.w,
    h: map.h,
    totalRooms: map.totalRooms,
    place_of_power: G.place_of_power,
    mapM: u8ToB64(map.m),
    mapR: u8ToB64(new Uint8Array(map.r.buffer, map.r.byteOffset, map.r.byteLength)),
    rooms: roomDTOs,
    creatures: serializeCreatures(),
    player: {
      expired_ms: G.expired_ms,
      player_x: G.checkpoint_x,
      player_y: G.checkpoint_y,
      scroll_x: G.scroll_x,
      scroll_y: G.scroll_y,
      magic_circuit: G.magic_circuit,
      checkpoint_x: G.checkpoint_x,
      checkpoint_y: G.checkpoint_y,
      player_walk_speed: G.player_walk_speed,
      wlk_wait: G.wlk_wait,
      circuit_fillrate: G.circuit_fillrate,
      circuit_recoverrate: G.circuit_recoverrate,
      explored: G.explored,
      player_shield: G.player_shield,
      shield_recover: G.shield_recover,
      shield_hp: G.shield_hp,
      player_gems: G.player_gems,
      checkpoints_found: G.checkpoints_found,
      player_hp: G.player_hp,
      player_lives: G.player_lives,
      player_lives_part: G.player_lives_part,
      current_boss: G.current_boss,
      training: G.training,
      agate_knife_loc: G.agate_knife_loc,
    },
    artifacts: G.artifacts.slice(),
  };

  const json = JSON.stringify(data);
  const gz = pako.gzip(json);
  return u8ToB64(gz);
}

// --- Load (equivalent to LoadGame; applied in the DungeonPlay steps) ------
export function loadFromString(b64: string): void {
  const gz = b64ToU8(b64);
  const json = pako.ungzip(gz, { to: 'string' }) as string;
  pending = JSON.parse(json) as SaveData;
  G.game_load = 1;
  getActiveWorld();
}

export function hasPendingLoad(): boolean {
  return pending !== null;
}

// ReadMapData(): called by RandomGenerateMap when game_load is set.
export function applySavedMap(): void {
  if (!pending) return;
  const d = pending;
  map.w = d.w;
  map.h = d.h;
  map.totalRooms = d.totalRooms;
  G.total_rooms = d.totalRooms;
  G.place_of_power = d.place_of_power;

  const m = b64ToU8(d.mapM);
  map.m.set(m);
  const rBytes = b64ToU8(d.mapR);
  new Uint8Array(map.r.buffer, map.r.byteOffset, map.r.byteLength).set(rBytes);

  for (let i = 0; i < d.rooms.length; i++) {
    const rd = d.rooms[i];
    const rm = rooms[i];
    rm.x = rd.x;
    rm.y = rd.y;
    rm.w = rd.w;
    rm.h = rd.h;
    rm.creator = rd.creator;
    rm.visited = rd.visited;
    rm.checkpoint = rd.checkpoint;
    rm.s_dist = rd.s_dist;
    rm.connections = rd.connections;
    rm.room_type = rd.room_type;
    rm.room_param = rd.room_param;
    rm.enemies = 0;
    let head: RoomConnection | null = null;
    for (const c of rd.con) {
      head = { x: c.x, y: c.y, x2: c.x2, y2: c.y2, c: c.c, n: head };
    }
    rm.con = head;
  }
}

function migrateV1Player(p: Record<string, number>): void {
  p.player_x = S(p.player_x);
  p.player_y = S(p.player_y);
  p.scroll_x = S(p.scroll_x);
  p.scroll_y = S(p.scroll_y);
  p.checkpoint_x = S(p.checkpoint_x);
  p.checkpoint_y = S(p.checkpoint_y);
  p.player_walk_speed = S(p.player_walk_speed);
}

// ReadCreatureData() hook.
export function applySavedCreatures(): void {
  if (!pending) return;
  const coordScale = pending.v === 1 ? RES_SCALE : 1;
  loadCreatures(pending.creatures, coordScale);
}

// ReadPlayerData().
export function applySavedPlayer(): void {
  if (!pending) return;
  const p = pending.player;
  if (pending.v === 1) migrateV1Player(p);
  G.expired_ms = p.expired_ms;
  G.player_x = p.player_x;
  G.player_y = p.player_y;
  G.scroll_x = p.scroll_x;
  G.scroll_y = p.scroll_y;
  G.magic_circuit = p.magic_circuit;
  G.checkpoint_x = p.checkpoint_x;
  G.checkpoint_y = p.checkpoint_y;
  G.player_walk_speed = p.player_walk_speed;
  G.wlk_wait = p.wlk_wait;
  G.circuit_fillrate = p.circuit_fillrate;
  G.circuit_recoverrate = p.circuit_recoverrate;
  G.explored = p.explored;
  G.player_shield = p.player_shield;
  G.shield_recover = p.shield_recover;
  G.shield_hp = p.shield_hp;
  G.player_gems = p.player_gems;
  G.checkpoints_found = p.checkpoints_found;
  G.player_hp = p.player_hp;
  G.player_lives = p.player_lives;
  G.player_lives_part = p.player_lives_part;
  G.current_boss = p.current_boss;
  G.training = p.training;
  G.agate_knife_loc = p.agate_knife_loc;
  for (let i = 0; i < 12; i++) G.artifacts[i] = pending.artifacts[i];
}

export function closeLoad(): void {
  pending = null;
}

// Progress feedback used by callers during long serialisation.
export function savingProgress(part: number, p: number): void {
  SavingScreen(part, p);
}
